import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * How far back the PAST tab reaches when nothing narrows it.
 *
 * An unbounded PAST asks a long-running competition for its entire history to
 * render one tab, so the window is floored rather than left open.
 */
export const PAST_WINDOW_FALLBACK_YEARS = 2

/**
 * The lower bound of the PAST tab's date window.
 *
 * When a schedule is selected, the tab cannot contain anything earlier than
 * that schedule began — so its `starts_at` is the honest floor, and it is used
 * whether it widens the window (a competition older than the fallback) or
 * narrows it (one that started last month). With no schedule selected — or one
 * that has not started yet, whose start would sit LATER than this window's end
 * and invert it — the window falls back to `PAST_WINDOW_FALLBACK_YEARS` years
 * back.
 *
 * The schedule-anchored value is a UTC-mode `Dayjs`; the fallback stays in the
 * viewer's zone, since "two years back from today" is viewer-relative by
 * nature while a schedule's start date is not.
 *
 * Returns a `Dayjs` rather than a formatted string because the call sites
 * disagree on format — some send `YYYY-MM-DD`, others a UTC ISO instant — and
 * each must keep the shape its endpoint already expects.
 *
 * MUST be used for BOTH the games and the events request of a given tab. The
 * two windows are required to match: an events window bounded more tightly
 * than the games beside it strands rows the list believes exist.
 */
export const resolvePastWindowStart = (
  scheduleStartsAt?: string | null
): Dayjs => {
  const trimmed = scheduleStartsAt?.trim()

  if (trimmed) {
    // Floored in UTC, NOT the viewer's zone. `starts_at` comes back as midnight
    // UTC (`2026-06-15T00:00:00+00:00`), which is 5pm the PREVIOUS day in
    // Vancouver — a local floor would start the window on the 14th for every
    // viewer west of UTC. The schedule's own calendar date is what is meant.
    const scheduleStart = dayjs.utc(trimmed)
    // A malformed date from the API must not produce an `Invalid Date` query
    // param — fall through to the floor instead.
    if (scheduleStart.isValid()) {
      const flooredStart = scheduleStart.startOf('day')
      // A schedule that has not started yet holds no past games, and its start
      // is LATER than this window's end (today) — sending it would invert the
      // range and match nothing. The floor is the honest bound instead.
      if (!flooredStart.isAfter(dayjs())) {
        return flooredStart
      }
    }
  }

  return dayjs().subtract(PAST_WINDOW_FALLBACK_YEARS, 'year').startOf('day')
}

export default resolvePastWindowStart
